import {
  doc,
  updateDoc,
  serverTimestamp,
  getDoc,
  setDoc
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../firebase';

export async function joinCommunity(userId, goalCategory, veylId, userName, avatarId, language = 'en', ageGroup = 'adult') {
  try {
    if (!userId || !goalCategory || !ageGroup) {
      console.error('joinCommunity: missing required fields', { userId, goalCategory, ageGroup });
      return null;
    }

    const baseId = goalCategory.toLowerCase().replace(/\s+/g, '_').replace(/&/g, 'and');
    // Community is scoped by category + age group + language.
    // ageGroup is now always 'adult' (Veyl is 18+ only) — the segment is
    // kept in the ID format to avoid migrating existing community docs
    // and user.communityId references built on the old format.
    // e.g. fitness_and_health_adult_en
    const communityId = `${baseId}_${ageGroup}_${language}`;

    const communityRef = doc(db, 'communities', communityId);
    const communitySnap = await getDoc(communityRef);

    if (!communitySnap.exists()) {
      await setDoc(communityRef, {
        name: `${goalCategory} Community`,
        goalCategory,
        ageGroup,
        language,
        createdAt: serverTimestamp(),
        memberCount: 1
      });
    }

    await updateDoc(doc(db, 'users', userId), {
      communityId,
      communityJoinedAt: serverTimestamp()
    });

    return communityId;
  } catch (err) {
    console.error('Error joining community:', err);
    return null;
  }
}

export async function leaveCommunity(userId) {
  try {
    await updateDoc(doc(db, 'users', userId), {
      communityId: null,
      communityJoinedAt: null
    });
    return true;
  } catch (err) {
    console.error('Error leaving community:', err);
    return false;
  }
}

// ─────────────────────────────────────────────────────────────
// Match requests now run on the server (sendCommunityMatchRequest /
// respondToMatchRequest in functions/index.js). The browser can't
// edit the other person's veyl or create notifications, so these
// are thin wrappers that keep the same function names and return
// values CommunityView.js already expects.
// ─────────────────────────────────────────────────────────────

export async function sendMatchRequest(fromUserId, fromVeylId, fromName, fromAvatarId, toUserId, toVeylId, goalCategory) {
  try {
    const functions = getFunctions();
    const sendRequest = httpsCallable(functions, 'sendCommunityMatchRequest');
    const result = await sendRequest({ fromVeylId, toUserId, toVeylId });
    return result.data; // { success: true } or { success: false, reason: 'limit' | 'duplicate' }
  } catch (err) {
    console.error('Error sending match request:', err);
    return { success: false, reason: 'error' };
  }
}

export async function acceptMatchRequest(requestId, requestData, currentUserId) {
  try {
    const functions = getFunctions();
    const respond = httpsCallable(functions, 'respondToMatchRequest');
    await respond({ requestId, action: 'accept' });
    return true;
  } catch (err) {
    console.error('Error accepting match request:', err);
    return false;
  }
}

export async function declineMatchRequest(requestId, requestData, currentUserId) {
  try {
    const functions = getFunctions();
    const respond = httpsCallable(functions, 'respondToMatchRequest');
    await respond({ requestId, action: 'decline' });
    return true;
  } catch (err) {
    console.error('Error declining match request:', err);
    return false;
  }
}